import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  copySkill,
  createSkill,
  deleteSkill,
  skillDetailQueryOptions,
  skillLabel,
  skillsQueryOptions,
  updateSkill,
  type Skill,
  type SkillFormBody,
} from "@/modules/chat/queries/skills-queries";
import {
  personalSpaceQueryOptions,
  spacesListQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { CopyToSpaceDialog } from "./CopyToSpaceDialog";

type Tab = "all" | "personal" | "shared";
type Mode =
  | { kind: "catalogue" }
  | { kind: "new" }
  | { kind: "edit"; ref: string }
  | { kind: "view"; ref: string };

export function SkillsAdminPage() {
  // Catalog is cross-space: own personal + space skills + platform skills.
  const { data: skills = [], isLoading } = useQuery(skillsQueryOptions("all"));
  const [mode, setMode] = useState<Mode>({ kind: "catalogue" });

  if (mode.kind === "view") {
    return (
      <SkillBuilder
        mode="edit"
        refId={mode.ref}
        readOnly
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }
  if (mode.kind === "new" || mode.kind === "edit") {
    return (
      <SkillBuilder
        mode={mode.kind}
        refId={mode.kind === "edit" ? mode.ref : undefined}
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }

  return (
    <SkillsCatalogue
      skills={skills}
      isLoading={isLoading}
      onCreate={() => setMode({ kind: "new" })}
      onEdit={(ref) => setMode({ kind: "edit", ref })}
      onView={(ref) => setMode({ kind: "view", ref })}
    />
  );
}

interface CatalogueProps {
  skills: Skill[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (ref: string) => void;
  onView: (ref: string) => void;
}

function SkillsCatalogue({
  skills,
  isLoading,
  onCreate,
  onEdit,
  onView,
}: CatalogueProps) {
  const qc = useQueryClient();
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [spaceFilterId, setSpaceFilterId] = useState<string | null>(null);
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false);
  const spaceMenuRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [copyTarget, setCopyTarget] = useState<Skill | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (ref: string) => deleteSkill(ref),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills"] }),
  });

  // «Личный» навык = висит в личном пространстве. Сравниваем по id
  // /spaces/personal — иначе свежесозданное командное с одним участником
  // протекало бы в таб «Личные» (та же ловушка, что и в коннекторах).
  // Системные платформенные навыки сюда не попадают — у них вообще нет space.
  const isPersonalSkill = (s: Skill) => {
    if (s.scope !== "user") return false;
    if (!s.space) return true;
    return !!personalSpace && s.space.id === personalSpace.id;
  };

  const isSystemSkill = (s: Skill) => s.scope === "system";

  const filtered = useMemo(() => {
    const scoped = skills.filter((s) => {
      // Системные навыки — платформенные, не относятся к пространствам.
      // Видны только в «Все»; при включённом space-фильтре скрываются.
      if (isSystemSkill(s)) {
        return tab === "all" && !spaceFilterId;
      }
      if (spaceFilterId && s.space?.id !== spaceFilterId) return false;
      if (tab === "personal" && !isPersonalSkill(s)) return false;
      if (tab === "shared" && isPersonalSkill(s)) return false;
      return true;
    });
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.label || "").toLowerCase().includes(q) ||
        (s.description || "").toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skills, tab, spaceFilterId, search, personalSpace]);

  // If the picked space disappears (lost access), silently fall back to "all".
  useEffect(() => {
    if (spaceFilterId && !spaces.some((s) => s.id === spaceFilterId)) {
      setSpaceFilterId(null);
    }
  }, [spaces, spaceFilterId]);

  useEffect(() => {
    if (!spaceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (spaceMenuRef.current?.contains(e.target as Node)) return;
      setSpaceMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSpaceMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [spaceMenuOpen]);

  const spaceFilterLabel = spaceFilterId
    ? spaces.find((s) => s.id === spaceFilterId)?.name ?? "Все пространства"
    : "Все пространства";

  const sectionTitle =
    tab === "personal"
      ? "Мои навыки"
      : tab === "shared"
        ? "Командные навыки"
        : "Все навыки";

  function handleDelete(s: Skill) {
    if (!confirm(`Удалить навык «${skillLabel(s)}»?`)) return;
    deleteMutation.mutate(s.ref);
    setOpenMenu(null);
  }

  function badgeFor(s: Skill): string | null {
    if (isSystemSkill(s)) return "Платформенный";
    if (isPersonalSkill(s)) return "Личное";
    return s.space?.name ?? null;
  }

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Каталог навыков</h1>
        <button type="button" className="cmd-page-create" onClick={onCreate}>
          <PlusIcon />
          <span>Создать навык</span>
        </button>
      </header>

      <div className="cmd-page-toolbar">
        <div className="history-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "all"}
            className={`history-tab${tab === "all" ? " active" : ""}`}
            onClick={() => setTab("all")}
          >
            Все
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "personal"}
            className={`history-tab${tab === "personal" ? " active" : ""}`}
            onClick={() => setTab("personal")}
          >
            Личные
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "shared"}
            className={`history-tab${tab === "shared" ? " active" : ""}`}
            onClick={() => setTab("shared")}
          >
            Командные
          </button>
        </div>
        <div className="history-filters">
          <div className="history-filter-popover-anchor" ref={spaceMenuRef}>
            <button
              type="button"
              className={`history-filter-select${spaceFilterId ? " is-applied" : ""}${spaceMenuOpen ? " is-open" : ""}`}
              onClick={() => setSpaceMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={spaceMenuOpen}
            >
              <span className="history-filter-select-label">{spaceFilterLabel}</span>
              <Chevron />
            </button>
            {spaceMenuOpen && (
              <div className="history-filter-popover" role="menu">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!spaceFilterId}
                  className={`history-filter-popover-item${!spaceFilterId ? " is-active" : ""}`}
                  onClick={() => {
                    setSpaceFilterId(null);
                    setSpaceMenuOpen(false);
                  }}
                >
                  Все пространства
                </button>
                {spaces.length > 0 && <div className="history-filter-popover-sep" />}
                {spaces.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={spaceFilterId === s.id}
                    className={`history-filter-popover-item${spaceFilterId === s.id ? " is-active" : ""}`}
                    onClick={() => {
                      setSpaceFilterId(s.id);
                      setSpaceMenuOpen(false);
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="cmd-search">
            <SearchIcon />
            <input
              type="search"
              className="cmd-search-input"
              placeholder="Название навыка"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <h2 className="cmd-page-section-title">{sectionTitle}</h2>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : filtered.length === 0 ? (
        <div className="cmd-empty">
          {search ? (
            <p>Ничего не найдено по запросу «{search}»</p>
          ) : tab === "personal" ? (
            <>
              <p className="cmd-empty-title">Личных навыков пока нет</p>
              <p className="cmd-empty-hint">
                Нажмите «Создать навык», чтобы добавить свой.
              </p>
            </>
          ) : tab === "shared" ? (
            <>
              <p className="cmd-empty-title">Командных навыков пока нет</p>
              <p className="cmd-empty-hint">
                Создайте навык в одном из своих пространств — он появится здесь.
              </p>
            </>
          ) : spaceFilterId ? (
            <p>В выбранном пространстве пока нет навыков.</p>
          ) : (
            <p>Навыков пока нет</p>
          )}
        </div>
      ) : (
        <div className="cmd-grid">
          {filtered.map((s) => (
            <SkillCard
              key={s.ref}
              skill={s}
              scopeBadge={badgeFor(s)}
              menuOpen={openMenu === s.ref}
              onMenuToggle={() =>
                setOpenMenu((m) => (m === s.ref ? null : s.ref))
              }
              onMenuClose={() => setOpenMenu(null)}
              onEdit={() => onEdit(s.ref)}
              onView={() => onView(s.ref)}
              onCopy={() => {
                setCopyTarget(s);
                setOpenMenu(null);
              }}
              onDelete={() => handleDelete(s)}
            />
          ))}
        </div>
      )}

      {copyTarget && (
        <CopyToSpaceDialog
          title="Скопировать навык"
          sourceLabel={skillLabel(copyTarget)}
          sourceSpaceId={copyTarget.space?.id ?? null}
          onCopy={(toSpaceId) => copySkill(copyTarget.ref, toSpaceId)}
          onClose={() => setCopyTarget(null)}
          invalidateKeys={[["skills"]]}
        />
      )}
    </div>
  );
}

interface CardProps {
  skill: Skill;
  /** Where the skill lives, in the same plashka style as ConnectorCard:
   *  «Платформенный» for system, «Личное» for personal scope, the space
   *  name for team scope. `null` hides the badge (e.g. while
   *  /spaces/personal is loading). */
  scopeBadge: string | null;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onView: () => void;
}

function SkillCard({
  skill,
  scopeBadge,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onCopy,
  onDelete,
  onView,
}: CardProps) {
  const isSystem = skill.scope === "system";
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onMenuClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMenuClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuClose]);

  return (
    <div className="cmd-card">
      <div className="cmd-card-head">
        <div className="cmd-card-head-main">
          <span className="cmd-card-name">{skillLabel(skill)}</span>
          {scopeBadge && (
            <span className="cmd-card-badge">{scopeBadge}</span>
          )}
        </div>
        <div className="cmd-card-menu-wrap" ref={wrapRef}>
          <button
            type="button"
            className="cmd-card-kebab"
            onClick={onMenuToggle}
            aria-label="Действия"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <KebabIcon />
          </button>
          {menuOpen && (
            <div className="cmd-card-menu" role="menu">
              {isSystem ? (
                <button
                  type="button"
                  role="menuitem"
                  className="cmd-card-menu-item"
                  onClick={() => {
                    onMenuClose();
                    onView();
                  }}
                >
                  <EyeIcon />
                  <span>Открыть</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => {
                      onMenuClose();
                      onEdit();
                    }}
                  >
                    <EditIcon />
                    <span>Редактировать</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={onCopy}
                  >
                    <CopyIcon />
                    <span>Скопировать</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item cmd-card-menu-item--danger"
                    onClick={() => {
                      onMenuClose();
                      onDelete();
                    }}
                  >
                    <TrashIcon />
                    <span>Удалить</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="cmd-card-desc">{skill.description || "Без описания"}</p>
    </div>
  );
}

interface BuilderProps {
  mode: "new" | "edit";
  refId?: string;
  onClose: () => void;
  readOnly?: boolean;
}

interface FormState extends SkillFormBody {
  /** Editor-side scope. `null` = personal, string = team space id. */
  spaceId: string | null;
}

const EMPTY_FORM: FormState = {
  name: "",
  label: "",
  description: "",
  content: "",
  spaceId: null,
};

function SkillBuilder({
  mode,
  refId,
  onClose,
  readOnly = false,
}: BuilderProps) {
  const qc = useQueryClient();
  const isNew = mode === "new";
  const { data, isLoading } = useQuery({
    ...skillDetailQueryOptions(refId ?? ""),
    enabled: !!refId,
  });
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const activeSpaceId = useSpaceStore((s) => s.currentSpaceId);

  // Seed the new-form scope from the sidebar's active space — same logic
  // as ConnectorBuilder. `spacesListQueryOptions()` filters out the
  // personal space, so any active id missing from that list falls back
  // to personal (null).
  const seedSpaceId = useMemo(() => {
    if (!activeSpaceId) return null;
    return spaces.some((s) => s.id === activeSpaceId) ? activeSpaceId : null;
  }, [activeSpaceId, spaces]);

  const [form, setForm] = useState<FormState>(() =>
    isNew ? { ...EMPTY_FORM, spaceId: seedSpaceId } : EMPTY_FORM,
  );
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState<string | null>(null);

  // Re-seed scope when /spaces resolves after the dialog opened. Only
  // applies to "new" mode and only before the user has interacted with
  // the select — picking "Личное" sets spaceId to null, which must not
  // be treated as "still unpicked" and snapped back to the seed.
  const userPickedSpaceRef = useRef(false);
  useEffect(() => {
    if (!isNew) return;
    if (userPickedSpaceRef.current) return;
    if (form.spaceId !== null) return;
    if (seedSpaceId !== null) {
      setForm((f) => ({ ...f, spaceId: seedSpaceId }));
    }
  }, [isNew, seedSpaceId, form.spaceId]);

  useEffect(() => {
    if (!loaded && data) {
      setForm({
        name: data.name,
        label: data.label ?? "",
        description: data.description,
        content: data.content,
        spaceId: data.space_id ?? data.space?.id ?? null,
      });
      setLoaded(true);
    }
  }, [loaded, data]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["skills"] });

  const createMutation = useMutation({
    mutationFn: (body: SkillFormBody) => createSkill(body),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(humanize(e.message)),
  });
  const updateMutation = useMutation({
    mutationFn: ({ ref, body }: { ref: string; body: SkillFormBody }) =>
      updateSkill(ref, body),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(humanize(e.message)),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave =
    form.name.trim().length > 0 &&
    form.content.trim().length > 0 &&
    !isPending;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  }

  function handleSave() {
    setError(null);
    const slug = form.name.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(slug)) {
      setError("Имя: 1–64 символа, латиница, цифры, дефисы.");
      return;
    }
    if (!form.content.trim()) {
      setError("Инструкции не могут быть пустыми.");
      return;
    }
    const body: SkillFormBody = {
      name: slug,
      label: form.label,
      description: form.description,
      content: form.content,
      space_id: form.spaceId,
    };
    if (isNew) createMutation.mutate(body);
    else if (refId) updateMutation.mutate({ ref: refId, body });
  }

  const title = isNew
    ? "Новый навык"
    : data
      ? skillLabel(data)
      : (refId ?? "Навык");

  return (
    <div className="cmd-builder">
      <header className="cmd-builder-topbar">
        <button
          type="button"
          className="cmd-builder-back"
          onClick={onClose}
          aria-label="Назад"
        >
          <ArrowLeftIcon />
        </button>
        <span className="cmd-builder-topbar-title">{title}</span>
      </header>

      <div className="cmd-builder-body">
        <p className="cmd-builder-intro">
          Навык — это набор инструкций в формате Markdown, которые агент применяет в подходящем контексте.
        </p>

        {error && <div className="cmd-builder-error">{error}</div>}

        <div className="cmd-builder-fields">
          <Field label="Имя (slug)">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="my-skill"
              autoComplete="off"
              readOnly={readOnly || !isNew}
            />
          </Field>

          <Field label="Название">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.label}
              onChange={(e) => update("label", e.target.value)}
              placeholder="Мой навык"
              maxLength={120}
              readOnly={readOnly}
            />
          </Field>

          <Field label="Описание">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Что делает этот навык"
              maxLength={240}
              readOnly={readOnly}
            />
          </Field>

          <Field
            label="Пространство"
            hint={
              form.spaceId
                ? "Навык будет доступен всем агентам в этом пространстве."
                : "Личный навык — виден только в ваших агентах."
            }
          >
            <select
              className="cmd-builder-input"
              value={form.spaceId ?? ""}
              onChange={(e) => {
                userPickedSpaceRef.current = true;
                update(
                  "spaceId",
                  e.target.value === "" ? null : e.target.value,
                );
              }}
              disabled={readOnly}
            >
              <option value="">Личное</option>
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Инструкции (Markdown)">
            <textarea
              className="cmd-builder-textarea"
              value={isLoading && !isNew ? "Загрузка…" : form.content}
              onChange={(e) => update("content", e.target.value)}
              placeholder={"## Обзор\n\nОписание навыка и инструкции агенту…"}
              spellCheck={false}
              readOnly={readOnly}
            />
          </Field>
        </div>

        {!readOnly && (
          <div className="cmd-builder-actions">
            <button
              type="button"
              className="cmd-page-create"
              onClick={handleSave}
              disabled={!canSave}
            >
              {isPending ? "Сохранение…" : "Сохранить"}
            </button>
            <button
              type="button"
              className="cmd-builder-cancel"
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="cmd-builder-field">
      <span className="cmd-builder-field-label">{label}</span>
      {children}
      {hint && <span className="cmd-builder-field-hint">{hint}</span>}
    </label>
  );
}

function humanize(msg: string): string {
  const m = msg.match(/"detail":"([^"]+)"/);
  if (m && m[1]) return m[1];
  return msg.slice(0, 200);
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 3.5C4.5 3.5 1.83 6.04 1 8c.83 1.96 3.5 4.5 7 4.5s6.17-2.54 7-4.5c-.83-1.96-3.5-4.5-7-4.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M14.1666 3.21354C14.1667 2.84774 14.021 2.49697 13.7623 2.23828C13.5038 1.9797 13.1534 1.83411 12.7877 1.83398C12.4221 1.83394 12.0711 1.97918 11.8125 2.23763L10.7116 3.33854L12.6621 5.28906L13.7623 4.1888C14.021 3.9302 14.1666 3.5793 14.1666 3.21354ZM2.91469 11.1374C2.81831 11.2335 2.74583 11.3517 2.7057 11.4818L2.70635 11.4824L1.91599 14.0833L4.51885 13.2943C4.64934 13.2546 4.76861 13.1835 4.86521 13.0872L11.955 5.99609L10.0045 4.04557L2.91469 11.1374ZM15.1666 3.21354C15.1666 3.80518 14.9461 4.37413 14.5508 4.81055L14.4694 4.89583L13.069 6.29557C13.0541 6.31581 13.0385 6.33586 13.0202 6.35417C13.0018 6.37247 12.9818 6.38812 12.9616 6.40299L5.57094 13.7956C5.38501 13.9809 5.16184 14.1242 4.91664 14.2155L4.80987 14.2513L1.90752 15.1315H1.90622C1.76223 15.1747 1.60931 15.1786 1.46351 15.1419C1.31762 15.1052 1.18456 15.0295 1.0781 14.9232C0.971689 14.8169 0.895629 14.6842 0.858696 14.5384C0.821783 14.3926 0.825387 14.2392 0.868462 14.0951L0.869113 14.0931L1.74932 11.1914L1.74997 11.1901C1.83808 10.9029 1.99556 10.6418 2.20831 10.4297L9.59698 3.03841C9.61183 3.01821 9.62818 2.99874 9.64646 2.98047C9.66473 2.9622 9.6842 2.94584 9.7044 2.93099L11.1048 1.5306C11.551 1.08446 12.1567 0.833905 12.7877 0.833984C13.4187 0.834109 14.0239 1.08504 14.47 1.53125C14.9161 1.97749 15.1667 2.58261 15.1666 3.21354Z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.33301 0.832031C9.82896 0.832031 10.2833 1.07533 10.6035 1.39551C10.9237 1.71568 11.167 2.17007 11.167 2.66602V3.49902H14C14.2761 3.49902 14.5 3.72288 14.5 3.99902C14.5 4.27517 14.2761 4.49902 14 4.49902H13.167V13.332C13.167 13.828 12.9237 14.2824 12.6035 14.6025C12.2833 14.9227 11.829 15.166 11.333 15.166H4.66699C4.17104 15.166 3.71666 14.9227 3.39648 14.6025C3.07631 14.2824 2.83301 13.828 2.83301 13.332V4.49902H2C1.72386 4.49902 1.5 4.27517 1.5 3.99902C1.5 3.72288 1.72386 3.49902 2 3.49902H4.83301V2.66602C4.83301 2.17007 5.07631 1.71568 5.39648 1.39551C5.71666 1.07533 6.17104 0.832031 6.66699 0.832031H9.33301ZM3.83301 13.332C3.83301 13.5027 3.92369 13.7157 4.10352 13.8955C4.28334 14.0753 4.49627 14.166 4.66699 14.166H11.333C11.5037 14.166 11.7167 14.0753 11.8965 13.8955C12.0763 13.7157 12.167 13.5027 12.167 13.332V4.49902H3.83301V13.332ZM6.66699 6.83203C6.94288 6.83221 7.16682 7.05615 7.16699 7.33203V11.332C7.16699 11.6081 6.94299 11.8319 6.66699 11.832C6.39085 11.832 6.16699 11.6082 6.16699 11.332V7.33203C6.16717 7.05604 6.39096 6.83203 6.66699 6.83203ZM9.33301 6.83203C9.60904 6.83203 9.83283 7.05604 9.83301 7.33203V11.332C9.83301 11.6082 9.60915 11.832 9.33301 11.832C9.05702 11.8319 8.83301 11.6081 8.83301 11.332V7.33203C8.83318 7.05615 9.05712 6.83221 9.33301 6.83203ZM6.66699 1.83203C6.49627 1.83203 6.28334 1.92271 6.10352 2.10254C5.92369 2.28237 5.83301 2.4953 5.83301 2.66602V3.49902H10.167V2.66602C10.167 2.4953 10.0763 2.28237 9.89648 2.10254C9.71666 1.92271 9.50373 1.83203 9.33301 1.83203H6.66699Z" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3 5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
